var _effectorFileName = "/src/widgets/layouts/main/slider-cards/slider-cards.tsx";
import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { CardKey, Card as CardType } from '@/page-content/group-lessons/constants/cards';
import { Article } from '@/shared/components/article';
import { ContactModal } from '@/shared/components/contact-modal';
import CheckMarkIcon from '@/shared/components/icons/checkmark-icon';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Carousel, CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/shared/components/ui/carousel';
import { Progress } from '@/shared/components/ui/progress';
import { cn } from '@/shared/lib/cn';
interface SliderCardProps {
  cards: CardType[];
  quizResult?: CardKey;
}
interface CardItemProps {
  item: CardType;
  id?: string;
  isActive?: boolean;
}
const CardItem = (props: CardItemProps) => {
  const {
    item,
    id,
    isActive = false
  } = props;
  const {
    t
  } = useTranslation('slider-cards');
  return <Card className={cn('flex flex-col transition-all duration-300', isActive && 'border-2 border-brand-accent-normal shadow-md shadow-neutral-500 dark:border-brand-accent-light dark:shadow-neutral-600 sm:shadow-lg')} id={id}>
      <CardHeader className="min-h-[120px] pb-0 md:min-h-[140px]">
        <CardTitle className="mb-1 text-xl font-medium md:text-2xl">{t(item.titleKey)}</CardTitle>
        <CardDescription>{t(item.descriptionKey)}</CardDescription>
      </CardHeader>

      <CardContent className="leading-6">
        {item.pointKeys.length > 0 && <ul className="mt-2 min-h-[120px] space-y-2 text-sm md:min-h-[150px] md:text-base">
            {item.pointKeys.map(pointKey => <li className="flex items-start gap-2" key={pointKey}>
                <CheckMarkIcon className="mt-1 h-4 w-4 shrink-0 rounded-full bg-brand-accent-light p-[2px] text-white dark:bg-brand-accent-normal" />
                {t(pointKey)}
              </li>)}
          </ul>}

        {item.badgeKeys.length > 0 && <ul className="mt-6 flex flex-wrap gap-2">
            {item.badgeKeys.map(badgeKey => <li key={badgeKey}>
                <Badge className="rounded-sm border-brand-accent-normal bg-brand-accent-superLight px-2 py-1 font-mono text-black/80 hover:bg-brand-accent-superLight">
                  {t(badgeKey)}
                </Badge>
              </li>)}
          </ul>}
      </CardContent>

      <CardFooter className="h-full items-end">
        <ContactModal entryPoint="GROUP_LESSONS_CARDS">
          <Button variant="cta" className="w-full px-6" aria-label={t('aria.bookConsultation')}>
            {t('buttons.select')}
          </Button>
        </ContactModal>
      </CardFooter>
    </Card>;
};
export const SliderCards = (props: SliderCardProps) => {
  const {
    cards,
    quizResult
  } = props;
  const {
    t
  } = useTranslation('slider-cards');
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const progress = current * 100 / count;
  useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList().length);
    const currentCard = cards.findIndex(card => card.key === quizResult);
    setCurrent(currentCard + 1);
    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api, quizResult]);
  const activeCardIndex = quizResult ? cards.findIndex(card => card.key === quizResult) : -1;
  useEffect(() => {
    if (api && activeCardIndex >= 0 && activeCardIndex < cards.length) {
      api.scrollTo(activeCardIndex);
    }
  }, [api, activeCardIndex, cards.length]);
  return <Article id="group-lessons-carousel">
      <h2 className="mb-8 header-h2">{t('title')}</h2>

      <div className="hidden gap-4 md:grid md:grid-cols-2 lg:grid-cols-3">
        {cards.map((card, index) => <CardItem item={card} key={card.key} id={card.key} isActive={index === activeCardIndex} />)}

        <Card className="flex flex-col">
          <CardHeader className="min-h-[140px] pb-0">
            <CardTitle className="text-2xl font-medium">{t('notFoundCard.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p>{t('notFoundCard.description')}</p>
          </CardContent>
          <CardFooter className="h-full items-end">
            <ContactModal entryPoint="GROUP_LESSONS_CARDS">
              <Button variant="cta" className="w-full px-6" aria-label={t('aria.bookConsultation')}>
                {t('buttons.submitRequest')}
              </Button>
            </ContactModal>
          </CardFooter>
        </Card>
      </div>

      <div className="md:hidden">
        <Carousel opts={{
        align: 'center',
        loop: true,
        inViewThreshold: 0.5
      }} setApi={setApi} className="md:hidden">
          <CarouselContent className="min-h-[400px] pb-6 sm:pb-8">
            {cards.map((card, index) => {
            return <CarouselItem key={index} className="flex justify-center sm:basis-1/2 md:basis-[40%] lg:basis-[42%] xl:basis-1/3 2xl:basis-[28%]" id={card.key}>
                  <CardItem id={card.key} item={card} isActive={index === activeCardIndex} />
                </CarouselItem>;
          })}
          </CarouselContent>

          <div className="pointer-events-none absolute inset-y-0 left-0 hidden h-full bg-gradient-to-r from-background xss:block sm:w-1/4"></div>
          <div className="pointer-events-none absolute inset-y-0 -right-1 hidden h-full bg-gradient-to-l from-background xss:block sm:w-1/4"></div>

          <CarouselPrevious className="left-0 top-[calc(100%+0.5rem)] translate-y-0" />
          <CarouselNext className="left-2 top-[calc(100%+0.5rem)] translate-x-full translate-y-0" />
        </Carousel>

        <Progress value={progress} className="ml-auto mt-4 w-24" />
      </div>
    </Article>;
};